import React, { useState, useEffect } from "react";
import styled from "styled-components/macro";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useParams, useLocation, useHistory } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  Button as MuiButton,
  Paper,
  TextField as MuiTextField,
  Typography,
  Link
} from "@material-ui/core";
import { spacing } from "@material-ui/system";
import { Alert as MuiAlert } from "@material-ui/lab";
import * as Yup from "yup";
import { Formik } from "formik";
import {
  Spacer,
} from "_components/common";
import { generateCodeForEmail } from "_services/verifyService";
import axios from "axios";
import { axiosAuth } from "_utils/axios";
import * as types from "_constants";
import logo from 'img/brands/OTS-logo.png';
import { FormRedirect } from "./FormRedirect";

const Alert = styled(MuiAlert)(spacing);
const Button = styled(MuiButton)(spacing);

const TextField = styled(MuiTextField)(spacing);

const Wrapper = styled(Paper)`
  padding: ${(props) => props.theme.spacing(6)}px;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: ${(props) => props.theme.spacing(10)}px;
  }
`;

const calculateTimeLeft = (expiryTimeInMS) => {
  let d = new Date();
  let exp = new Date(d.getTime() + expiryTimeInMS);
  return exp.getTime() - d.getTime();
}

const formatToHHMM = (seconds) => {
  var hours = Math.floor(seconds / 3600);
  var minutes = Math.floor((seconds - (hours * 3600)) / 60);
  var seconds = seconds - (hours * 3600) - (minutes * 60);

  if (minutes < 10) { minutes = "0" + minutes; }
  if (seconds < 10) { seconds = "0" + seconds; }
  return minutes + ':' + seconds;
};

function parseJwt(token) {
  var base64Url = token.split('.')[1];
  var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  var jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
    return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
  }).join(''));

  return JSON.parse(jsonPayload);
};

function ConfirmCode() {
  const dispatch = useDispatch();

  const otp = useSelector(state => state.appReducer.otp);
  const { organizationSlug } = useParams();
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft(otp && otp.expiry || 0));
  const [timeUp, setTimeUp] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [disableAll, setDisableAll] = useState(false);
  const search = useLocation().search;
  const history = useHistory();
  const redirect_url = new URLSearchParams(search).get('redirect_url');
  const [formRedirect, setFormRedirect] = useState(null);

  const startTimer = () => {
    if (!isLoaded) {
      return;
    }

    let timer = null;
    if (otp !== undefined && otp.sessionId !== '') {
      timer = setTimeout(() => {
        if (timeLeft > 0) {
          setTimeLeft(timeLeft - 1000);
        } else {
          setTimeUp(true);
          clearTimeout(timer);
        }
      }, 1000);
    }

    // Clear timeout if the component is unmounted
    return () => {
      if (timer) {
        clearTimeout(timer)
      }
    };
  };

  useEffect(() => {
    if (redirect_url === undefined || redirect_url === null) {
      // redirect to 500 if no url is found
      history.push(`/500`);
    } else {
      setIsLoaded(true);
    }
  }, [redirect_url, search]);

  useEffect(() => {
    return startTimer();
  });

  if (!isLoaded) {
    return <></>;
  }

  if (otp === undefined || otp !== null && otp.sessionId === '') {
    return (
      <Wrapper>
        <Helmet title="Confirm Code | OnTheStage" />
        <Spacer my={3} />
        <div style={{width: '100%', textAlign: 'center'}}>
          <img alt="OTS LOGO" src={logo} style={{width: '256px'}} />
        </div>

        <Typography component="h2" variant="body1" align="center">
          Your session is not authorized or invalid, Please <Link component={NavLink} to={redirect_url}>
            go back
          </Link> to generate code page and redo again.
        </Typography>
      </Wrapper>
    );
  }

  const rensendCodeSubmit = async () => {
    try {
      const data = await generateCodeForEmail(otp.email, organizationSlug, otp.sessionId);
      dispatch({
        type: types.GENERATECODE_SUCCESS,
        payload: {
          email: otp.email,
          sessionId: data.sessionId,
          expiry: data.expiry
        }
      });
      // reset the timer
      setTimeLeft(otp.expiry);
      setTimeUp(false);
    } catch (error) {
      console.log(`confirmation error, `, error);
    }
  };

  if (formRedirect) {
    return <FormRedirect {...formRedirect} />
  }

  return (
    <Wrapper>
      <Helmet title="Confirm Code | OnTheStage" />
      <Spacer my={3} />
      <div style={{width: '100%', textAlign: 'center'}}>
        <img alt="OTS LOGO" src={logo} style={{width: '256px'}} />
      </div>

      <Typography component="h2" variant="body1" align="center">
        Enter your confirmation code:
      </Typography>

      <Spacer my={3} />
      {timeLeft > 0 && <Typography component="span" variant="body1" align="center">
        {formatToHHMM(timeLeft / 1000)}
      </Typography>
      }
      {timeLeft < 0 && <Alert mt={2} mb={1} severity="info">Timeout! Use resend code to get a new confirmation code</Alert>}

      <Formik
        initialValues={{
          confirmation_code: "",
          submit: false
        }}
        validationSchema={Yup.object().shape({
          confirmation_code: Yup.string()
            .max(12)
            .required("Confirmation Code is required"),
        })}
        onSubmit={async (values, { setErrors, setStatus, setSubmitting }) => {
          try {
            let data = {
              confirmationCode: values.confirmation_code,
              sessionId: otp.sessionId
            };

            const redirect_url = new URLSearchParams(search).get('redirect_url');
            const redirect_app_url = new URLSearchParams(search).get('redirect_app_url');
            const redirect_err_url = new URLSearchParams(search).get('redirect_err_url');
            const r = await axiosAuth.post(`confirm-code`, data);
            if (r.data) {
              // dispatch AUTH_SUCCESS
              await dispatch({
                type: types.AUTH_SIGN_IN_SUCCESS,
                payload: {
                  user: parseJwt(r.data.token)
                }
              });
              // redirect to success page
              localStorage.setItem('access_token', r.data.token);
              if (redirect_url.indexOf('http') > -1) {
                // let url = `${redirect_url}?token=${r.data.token}&redirect_app_url=${redirect_app_url}`;
                // if (redirect_err_url) {
                //   url = `${url}&redirect_err_url=${redirect_err_url}`;
                // }
                // if (window.opener) {
                //   window.opener.location.href = url;
                //   window.close();
                // } else {
                //   window.location.href = url;
                // }
                let formData = {
                  redirect_url: redirect_url,
                  token: r.data.token,
                  redirect_app_url: redirect_app_url,
                }
                if (redirect_err_url) {
                  formData = {
                    ...formData,
                    redirect_err_url: redirect_err_url
                  };
                }
                setFormRedirect(formData);
              } else {
                try {
                  // handling situation when user is logged in, but navigates to ticket page
                  const onstageAppAuthURL = new URL(process.env.REACT_APP_BASE_API_URL).origin;
                  const axiosAPI = axios.create({
                    baseURL: onstageAppAuthURL,
                    withCredentials: true,
                    validateStatus: (status) => status === 200,
                  });
                  await axiosAPI.get(`/${organizationSlug}/auth-subscription?token=${r.data.token}`);
                } catch (error) {
                  console.log(`onstage auth-subscription error: `, error);
                }
                history.push(redirect_url);
              }
            } else {
              // error case, show the error message to the user
            }
          } catch (error) {
            const message = error.response?.data || error.message || "Something went wrong";
            setStatus({ success: false });
            setErrors({ submit: message });
            setSubmitting(false);
          }
        }}
      >
        {({
          errors,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          touched,
          values,
          setFieldValue,
        }) => (
          <form noValidate onSubmit={handleSubmit}>
            {errors.submit && (
              <Alert mt={2} mb={1} severity="warning">
                {errors.submit}
              </Alert>
            )}
            <TextField
              autoFocus
              name="confirmation_code"
              variant="outlined"
              label="Confirmation Code"
              value={values.confirmation_code}
              disabled={isSubmitting || timeUp}
              error={Boolean(touched.confirmation_code && errors.confirmation_code)}
              fullWidth
              helperText={touched.confirmation_code && errors.confirmation_code}
              onBlur={handleBlur}
              onChange={handleChange}
              inputProps={{ maxLength: 6 }}
              my={2}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              disabled={isSubmitting || timeUp}
              mb={2}
              mt={2}
            >
              Confirm
            </Button>

            <Button
              fullWidth
              variant="contained"
              color="primary"
              disabled={!timeUp}
              onClick={(e) => { rensendCodeSubmit() }}
              mb={2}
              mt={2}
            >
              Resend Code
            </Button>

            {timeUp &&
              <Typography component="h2" variant="body1" align="center">
                Your session is not authorized or invalid, Please <Link component={NavLink} to={redirect_url}>
                  go back
                </Link> to generate code page and redo again.
              </Typography>}

          </form>
        )}
      </Formik>
    </Wrapper >);
}

export default ConfirmCode;